// ---- tolerant parser: accepts JSON array, JSON object {intro, questions}, or legacy newline text
type ParsedQuestions = {
  intro?: string;
  questions: string[] | { questionNum: number; questionText: string }[];
  type?: 'stringArray' | 'objectArray';
};

export const isQuestionLine = (line: string) => /^\s*(\d{1,2}[.)]|[-–•*])\s+/.test(line); // "1. ", "1) ", "-", "•", etc.

export function parseQuestionsField(raw: string | null | undefined): ParsedQuestions {
  if (!raw) return { questions: [] };
  const text = raw.trim();

  // JSON? (array or object)
  if (text.startsWith('[') || text.startsWith('{')) {
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) {
        if (
          parsed[0] &&
          typeof parsed[0] === 'object' &&
          'questionNum' in parsed[0] &&
          'questionText' in parsed[0]
        ) {
          return {
            questions: parsed.map((q) => ({
              questionNum: q.questionNum,
              questionText: String(q.questionText).trim(),
            })),
            type: 'objectArray',
          };
        }
        return {
          questions: parsed.map((s) => String(s).trim()).filter(Boolean),
          type: 'stringArray',
        };
      }
      if (parsed && typeof parsed === 'object') {
        // const intro = typeof parsed.intro === 'string' ? parsed.intro.trim() : undefined;
        const qs = Array.isArray(parsed.questions)
          ? parsed.questions.map((s: unknown) => String(s).trim()).filter(Boolean)
          : [];
        return { questions: qs, type: 'stringArray' };
      }
    } catch {
      // fall through to text mode
    }
  }

  // Legacy text: split by lines, drop empty lines, strip optional intro
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length === 0) return { questions: [] };

  return { questions: lines, type: 'stringArray' };
}
// returns the parsed object or throws with context
export function parseRawResponse(input: unknown): any {
  if (input == null) return null;
  let s = String(input).trim();

  // strip triple quotes """..."""
  if (s.startsWith('"""') && s.endsWith('"""')) s = s.slice(3, -3).trim();

  // strip code fences ```json ... ``` (json/js/javascript)
  s = s
    .replace(/^```(?:json|js|javascript)?\s*/i, '')
    .replace(/```$/, '')
    .trim();

  // sometimes people prepend the word json before the object (e.g. "json\n{...}")
  if (s.toLowerCase().startsWith('json')) {
    s = s.slice(4).trim(); // remove 'json' (and possible newline)
  }

  // Try direct JSON first
  try {
    return JSON.parse(s);
  } catch {}

  // If it’s a quoted JSON string: "...{\"a\":1}..."
  const isQuoted = (q: string) => s.startsWith(q) && s.endsWith(q);
  if (isQuoted('"') || isQuoted("'")) {
    try {
      // First parse removes the outer quotes & escapes
      s = JSON.parse(s);
      // Fall through to final parse below
    } catch (e) {
      // not valid quoted JSON; continue to cleanup
      s = s.slice(1, -1);
    }
  }

  // Last chance: unescape common sequences then parse
  const cleaned = s
    .replace(/\\n/g, '\n')
    .replace(/\\r/g, '\r')
    .replace(/\\t/g, '\t')
    .replace(/\\"/g, '"')
    .replace(/\\\\/g, '\\')
    .trim();

  return JSON.parse(cleaned);
}
